import { useState } from "react";
import {
  CalendarDays,
  CheckCheck,
  Pencil,
  Send,
  Trash2,
  Undo2,
  UserRound,
} from "lucide-react";
import {
  useDelete,
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { formatDateOnly } from "../clients/clientModel";
import { useScrollIntoViewOnNarrow } from "./useScrollIntoViewOnNarrow";
import type {
  Client,
  ContentPiece,
  ContentPieceVersion,
  PieceStatus,
  Sale,
} from "../types";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { PieceComments } from "./PieceComments";
import { PieceFormDialog } from "./PieceFormDialog";
import { PieceVersions } from "./PieceVersions";
import { CONTENT_BUCKET, FORMAT_ICONS, STATUS_COLORS } from "./pieceModel";

export const PieceDetail = ({
  pieceId,
  clientName,
  onDeleted,
}: {
  pieceId: string | null;
  clientName: (companyId: Identifier) => string | undefined;
  onDeleted: () => void;
}) => {
  const translate = useTranslate();
  if (!pieceId) {
    return (
      <aside className="rounded-[22px] bg-card p-6 text-sm text-muted-foreground">
        {translate("resources.content_pieces.no_selection")}
      </aside>
    );
  }
  return (
    <PieceDetailContent
      key={pieceId}
      pieceId={pieceId}
      clientName={clientName}
      onDeleted={onDeleted}
    />
  );
};

const PieceDetailContent = ({
  pieceId,
  clientName,
  onDeleted,
}: {
  pieceId: string;
  clientName: (companyId: Identifier) => string | undefined;
  onDeleted: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const notify = useNotify();
  const [isEditing, setIsEditing] = useState(false);
  const [update, { isPending: isUpdating }] = useUpdate<ContentPiece>();
  const [deleteOne] = useDelete<ContentPiece>();

  const { data: piece } = useGetOne<ContentPiece>("content_pieces", {
    id: pieceId,
  });
  const { data: assignee } = useGetOne<Sale>(
    "sales",
    { id: piece?.assignee_id ?? 0 },
    { enabled: piece?.assignee_id != null },
  );
  const { data: clients = [] } = useGetList<Client>("clients", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: versions = [] } = useGetList<ContentPieceVersion>(
    "content_piece_versions",
    {
      filter: { piece_id: pieceId },
      sort: { field: "version_number", order: "DESC" },
      pagination: { page: 1, perPage: 100 },
    },
  );

  const detailRef = useScrollIntoViewOnNarrow<HTMLElement>(piece != null);

  if (!piece) return <aside className="rounded-[22px] bg-card p-6" />;

  const FormatIcon = FORMAT_ICONS[piece.format];

  const setStatus = (status: PieceStatus) =>
    update(
      "content_pieces",
      { id: piece.id, data: { status }, previousData: piece },
      {
        mutationMode: "pessimistic",
        onError: () => notify("ra.notification.http_error", { type: "error" }),
      },
    );

  const handleDelete = async () => {
    if (!window.confirm(translate("resources.content_pieces.delete_confirm"))) {
      return;
    }
    const filePaths = versions
      .map((version) => version.file_path)
      .filter((path): path is string => Boolean(path));
    if (filePaths.length > 0) {
      await getSupabaseClient().storage.from(CONTENT_BUCKET).remove(filePaths);
    }
    deleteOne(
      "content_pieces",
      { id: piece.id, previousData: piece },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("resources.content_pieces.deleted", { type: "success" });
          onDeleted();
        },
      },
    );
  };

  return (
    <aside
      ref={detailRef}
      className="flex scroll-mt-4 flex-col gap-5 rounded-[22px] bg-card p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold leading-snug tracking-tight">
            {piece.title}
          </h2>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <FormatIcon className="size-3.5" />
              {translate(`resources.content_pieces.formats.${piece.format}`)}
            </span>
            {piece.publish_date ? (
              <span className="flex items-center gap-1">
                <CalendarDays className="size-3.5" />
                {formatDateOnly(piece.publish_date, locale)}
              </span>
            ) : null}
            {assignee ? (
              <span className="flex items-center gap-1">
                <UserRound className="size-3.5" />
                {assignee.first_name} {assignee.last_name}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {clientName(piece.company_id)}
          </div>
        </div>
        <StatusPill status={piece.status} />
      </div>

      {piece.status === "approved" && piece.approved_by ? (
        <p className="rounded-xl bg-[#3dd68c]/10 px-3 py-2 text-xs text-[#3dd68c]">
          {translate("resources.content_pieces.approved_by", {
            name: piece.approved_by,
          })}
        </p>
      ) : null}

      <PieceVersions
        piece={piece}
        versions={versions}
        accountName={clientName(piece.company_id)}
      />

      {piece.script ? (
        <TextBlock label={translate("resources.content_pieces.fields.script")}>
          {piece.script}
        </TextBlock>
      ) : null}
      {piece.caption && versions.length === 0 ? (
        <TextBlock label={translate("resources.content_pieces.fields.caption")}>
          {piece.caption}
        </TextBlock>
      ) : null}

      <PieceComments pieceId={piece.id} />

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        {piece.status === "preparing" ||
        piece.status === "changes_requested" ? (
          <Button
            className="rounded-full"
            disabled={isUpdating}
            onClick={() => setStatus("in_review")}
          >
            <Send className="size-4" />
            {translate(
              piece.status === "changes_requested"
                ? "resources.content_pieces.action.resend_to_review"
                : "resources.content_pieces.action.send_to_review",
            )}
          </Button>
        ) : null}
        {piece.status === "approved" ? (
          <Button
            className="rounded-full"
            disabled={isUpdating}
            onClick={() => setStatus("published")}
          >
            <CheckCheck className="size-4" />
            {translate("resources.content_pieces.action.mark_published")}
          </Button>
        ) : null}
        {piece.status === "in_review" ? (
          <Button
            variant="outline"
            className="rounded-full"
            disabled={isUpdating}
            onClick={() => setStatus("preparing")}
          >
            <Undo2 className="size-4" />
            {translate("resources.content_pieces.action.back_to_preparing")}
          </Button>
        ) : null}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="size-4" />
            {translate("resources.content_pieces.action.edit")}
          </Button>
          <Button
            variant="ghost"
            className="rounded-full text-destructive"
            aria-label={translate("resources.content_pieces.action.delete")}
            onClick={handleDelete}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <PieceFormDialog
          clients={clients}
          piece={piece}
          onClose={() => setIsEditing(false)}
          onSaved={() => setIsEditing(false)}
        />
      ) : null}
    </aside>
  );
};

export const StatusPill = ({ status }: { status: PieceStatus }) => {
  const translate = useTranslate();
  const color = STATUS_COLORS[status];
  return (
    <span
      className="shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold"
      style={{ background: `${color}26`, color }}
    >
      {translate(`resources.content_pieces.statuses.${status}`)}
    </span>
  );
};

const TextBlock = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {label}
    </div>
    <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">
      {children}
    </p>
  </div>
);
